import { plgRequest, warmPlgProof } from "plg/api/plgApi";
import { extractErrorCode, extractErrorMessage, toApiResult } from "util/apiError";
import { isViewLimitCode, ViewLimitError } from "components/Screens/Marketplace/Customer/common/viewLimit";
import type { ApiResult } from "util/apiError";
import type { TalentApi } from "components/Screens/Marketplace/Customer/TalentApiContext";
import type {
  CreateInterviewRoundBody,
  InterviewRequestPayload,
  InterviewRound,
  PreferencesBody,
  ShortlistResponse,
  SkillOption,
  TalentListParams,
  TalentListResponse,
  TalentFacets,
  TalentProfile,
  ToolOption,
  ArbiterRole,
  AiSummaryFeedbackBody,
} from "components/Screens/Marketplace/Customer/types";
import type { AnalyticsEventInput } from "services/network/talentAnalytics";
import type { ShortlistFilters } from "components/Screens/Marketplace/Customer/Shortlist/shortlistFilters";

type Services = Omit<
  TalentApi,
  "kind" | "user" | "hasPermission" | "routes" | "customerState" | "talentNetworkVisible" | "userInfoLoading" | "renderAccountMenu"
>;

const enc = encodeURIComponent;

/**
 * PRD-3334 "Request a meeting": one per customer (409 MEETING_ALREADY_REQUESTED). The portal stores it with
 * the shortlist as it stands (read server-side), emails the customer and sends the lead to Salesforce.
 */
export const MEETING_REQUEST_PATH = "/v1/plg/meeting-requests";
export const MEETING_ALREADY_REQUESTED = "MEETING_ALREADY_REQUESTED";

export interface MeetingRequestBody {
  /** "What you are looking for" tiles, when asked. */
  preferenceTiles?: { cat: string; label: string }[];
  /** "When do you need someone in place?" (may be skipped) */
  urgency?: string;
  /** "Do you have a VoIP system?" (may be skipped) */
  voip?: string;
  /** "How does your team access your EMR?" (may be skipped) */
  emrAccess?: string;
  /** "When suits you?": a weekday (YYYY-MM-DD), a time ("10:00 AM") and the customer's time zone ("CT"). */
  slot: { date: string; time: string; timeZone: string };
  note?: string;
  /** Keys of the home page Edge Standard tiles opened (PRD-3334, for the Salesforce lead). */
  edgeStandardTilesClicked?: string[];
}

export interface MeetingRequestSummary {
  id: string;
  requestedAt: string;
  shortlistCount: number;
}

export async function submitMeetingRequest(body: MeetingRequestBody): Promise<ApiResult<MeetingRequestSummary>> {
  return toApiResult(await plgRequest("POST", MEETING_REQUEST_PATH, body));
}

/** The customer's meeting request, or null until one is sent. */
export async function fetchMeetingRequest(): Promise<MeetingRequestSummary | null> {
  const { ok, status, payload } = await plgRequest<{ request: MeetingRequestSummary | null }>("GET", MEETING_REQUEST_PATH);
  if (!ok) throw new Error(`Failed to load the meeting request: ${status}`);
  return payload?.request ?? null;
}

async function getOrThrow<T>(path: string, what: string): Promise<T> {
  const { ok, status, payload } = await plgRequest<T>("GET", path);
  if (!ok) throw new Error(`Failed to fetch ${what}: ${status}`);
  return payload;
}

/** A capped open (429 VIEW_LIMIT_*) becomes a ViewLimitError so the profile page can show the wall. */
async function fetchProfile(talentId: string): Promise<TalentProfile> {
  const { ok, status, payload } = await plgRequest<TalentProfile>("GET", `/talents/${enc(talentId)}/profile/portal`);
  if (ok) return payload;
  const code = extractErrorCode(payload);
  if (isViewLimitCode(code)) throw new ViewLimitError(code, extractErrorMessage(payload) || "You have hit today's viewing limit.");
  throw new Error(`Failed to fetch talent profile: ${status}`);
}

function listQuery(params?: TalentListParams): string {
  const q = new URLSearchParams();
  if (params?.page !== undefined) q.append("page", String(params.page));
  if (params?.limit !== undefined) q.append("limit", String(params.limit));
  if (params?.country) q.append("country", params.country);
  const join = (v?: string | string[]) => (Array.isArray(v) ? v.join(",") : v);
  if (join(params?.language)) q.append("language", join(params?.language) as string);
  if (join(params?.skill)) q.append("skill", join(params?.skill) as string);
  if (join(params?.tool)) q.append("tool", join(params?.tool) as string);
  if (join(params?.role)) q.append("role", join(params?.role) as string);
  if (join(params?.arbiterRole)) q.append("arbiterRole", join(params?.arbiterRole) as string);
  // customerId is never sent: the backend takes it from the PLG token.
  const qs = q.toString();
  return `/talents/portal${qs ? `?${qs}` : ""}`;
}

export const plgTalentServices: Services = {
  fetchTalentList: (params?: TalentListParams) => getOrThrow<TalentListResponse>(listQuery(params), "talents"),
  fetchTalentProfile: (talentId: string) => {
    // Time on profile is reported as the page closes, when there's no time to sign a proof.
    warmPlgProof("POST", "/talent-analytics/events");
    return fetchProfile(talentId);
  },
  fetchTalentPdf: async (talentId: string) => {
    const { ok, status, payload } = await plgRequest<Blob>("GET", `/talents/${enc(talentId)}/profile/portal?pdf=true`, undefined, { blob: true });
    if (!ok) throw new Error(`Download failed: ${status}`);
    return { blob: payload, filename: `${talentId}_Edge_resume.pdf` };
  },
  fetchSkills: () => getOrThrow<SkillOption[]>("/talents/skills", "skills"),
  fetchTools: () => getOrThrow<ToolOption[]>("/talents/tools", "tools"),
  fetchRoles: () => getOrThrow<ArbiterRole[]>("/talents/roles", "roles"),
  fetchTalentFacets: () => getOrThrow<TalentFacets>("/talents/portal/facets", "filter options"),
  requestInterview: async (data: InterviewRequestPayload) => {
    const { ok, status, payload } = await plgRequest("POST", "/talents/interview-request", data);
    if (!ok) throw new Error(`Failed to request interview: ${status}`);
    return payload;
  },
  checkInterviewRequested: async (_customerId: string, talentId: string) => {
    const { ok, payload } = await plgRequest<{ requested?: boolean }>("GET", `/talents/interview-check?talentId=${enc(talentId)}`);
    return ok ? !!payload?.requested : false;
  },
  submitHireRequest: async (data: Record<string, any>) => {
    const { ok, status, payload } = await plgRequest("POST", "/talents/hire-request", data);
    if (!ok) throw new Error(`Failed to submit hire request: ${status}`);
    return payload;
  },
  fetchShortlist: () => getOrThrow<ShortlistResponse>("/talents/shortlist", "shortlist"),
  addToShortlist: async (talentId: string, filters?: ShortlistFilters | null): Promise<ApiResult<unknown>> =>
    toApiResult(await plgRequest("POST", "/talents/shortlist", { talentId, ...(filters ? { filters } : {}) })),
  removeFromShortlist: async (talentId: string) => {
    const { ok, status } = await plgRequest("DELETE", `/talents/shortlist/${enc(talentId)}`);
    if (!ok) throw new Error(`Failed to remove from shortlist: ${status}`);
  },
  submitAiSummaryFeedback: async (talentId: string, body: AiSummaryFeedbackBody) =>
    toApiResult(await plgRequest("POST", `/talents/${enc(talentId)}/ai-summary-feedback`, body)),
  fetchInterviewRounds: async () => {
    const payload = await getOrThrow<InterviewRound[] | { rounds?: InterviewRound[] }>("/talents/interview-rounds", "interview rounds");
    return Array.isArray(payload) ? payload : payload?.rounds || [];
  },
  createInterviewRound: async (body: CreateInterviewRoundBody) =>
    toApiResult<InterviewRound>(await plgRequest("POST", "/talents/interview-rounds", body)),
  postPreferences: async (body: PreferencesBody) => {
    await plgRequest("POST", "/talents/preferences", body);
  },
  ingestAnalyticsEvent: async (input: AnalyticsEventInput, opts?: { keepalive?: boolean }) => {
    try {
      await plgRequest("POST", "/talent-analytics/events", input, { keepalive: opts?.keepalive });
    } catch {
      // Swallow — analytics failures must not affect the customer experience (same as the portal helper).
    }
  },
};
